import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import axiosInstance, { API_ENDPOINTS } from 'src/utils/axios';


export default function AllergenQuickForm({ current, open, onClose, isEdit }) {
  const { enqueueSnackbar } = useSnackbar();
  const schema = Yup.object().shape({
    portugueseName: Yup.string().required("Nome (Português) é obrigatório."),
    frenchName: Yup.string().optional(),
    englishName: Yup.string().optional(),
    icon: Yup.string().optional().nullable(),
    order: Yup.number().required("Ordem é obrigatória.").min(1, "Ordem deve ser maior que 0"),
  });

  const defaultData = useMemo(
    () => ({
      id: current ? current?.id : '',
      portugueseName: current ? current?.portugueseName : '',
      frenchName: current ? current?.frenchName : '',
      englishName: current ? current?.englishName : '',
      icon: current ? current?.icon : '',
      order: current ? current?.order : 1,
      creationDate: current ? current?.creationDate : '',
      modifiedAt: current ? current?.modifiedAt : ''
    }),
    [current]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultData,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Reset form values when current changes (when opening for edit)
  useEffect(() => {
    if (current && current.id) {
      reset({
        id: current.id,
        portugueseName: current.portugueseName || '',
        frenchName: current.frenchName || '',
        englishName: current.englishName || '',
        icon: current.icon || '',
        order: current.order || 1,
        creationDate: current.creationDate || '',
        modifiedAt: current.modifiedAt || ''
      });
    }
  }, [current, reset]);

  const onSubmit = useCallback(
    async (data) => {
      try {
        // Remove date fields that shouldn't be sent
        const { creationDate, modifiedAt, ...payload } = data;

        if (isEdit === false)
          await axiosInstance.post(API_ENDPOINTS.allergen.post, payload);
        else
          await axiosInstance.put(API_ENDPOINTS.allergen.update, payload);

        reset();
        onClose();
        enqueueSnackbar(`${isEdit ? 'Atualização' : 'Adicionado'} com sucesso!`);
      } catch (error) {
        console.error(error);
      }
    },
    [enqueueSnackbar, onClose, reset, isEdit]
  );

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: 720 },
      }}

    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{isEdit ? 'Atualizar' : 'Adicionar'} Alergénio</DialogTitle>

        <DialogContent>
          <Box
            rowGap={2}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(3, 1fr)',
            }}
            sx={{ p: 1 }}
          >
            <RHFTextField name="portugueseName" label="Nome (Português)" />
            <RHFTextField name="frenchName" label="Nome (Francês)" />
            <RHFTextField name="englishName" label="Nome (Inglês)" />
          </Box>
          <Box
            rowGap={2}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
            sx={{ p: 1 }}
          >
            <RHFTextField name="order" label="Ordem" type="number" helperText="Número que aparece na tabela (ex: 1, 2, 3...)" />
            <RHFTextField name="icon" label="Ícone (emoji ou texto)" helperText="Ex: 🥜 ou Glúten" />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Atualizar' : 'Adicionar'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

AllergenQuickForm.propTypes = {
  current: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  isEdit: PropTypes.bool
};
